// Core math for Process 2450 workouts.
// All helpers are pure functions so they're easy to test.

export const MIRROR_MAP = { 0: 5, 1: 6, 2: 7, 3: 8, 4: 9, 5: 0, 6: 1, 7: 2, 8: 3, 9: 4 };

export const mirrorDigit = (d) => MIRROR_MAP[Number(d)];

// Normalize seed to array of digits of length n (3 or 4).
export const parseSeed = (seed, length) => {
  const clean = String(seed || "").replace(/\D/g, "").slice(0, length);
  const padded = clean.padStart(length, "0");
  return padded.split("").map(Number);
};

// Rolling workout: seed appears on BOTH the first and last row. Walking DOWN
// adds 1 per digit with 9 -> 0 wrap, cycling exactly once (11 rows total).
// Seed 2450 produces, top -> bottom:
// 2450, 3561, 4672, 5783, 6894, 7905, 8016, 9127, 0238, 1349, 2450.
export const buildRollingColumns = (seedDigits) =>
  seedDigits.map((d) => {
    const col = [];
    let cur = d;
    for (let i = 0; i <= 10; i++) {
      col.push(cur);
      cur = cur === 9 ? 0 : cur + 1;
    }
    return col;
  });

// Mirror a combination digit-wise. mirrorMask is bool[] controlling which
// positions get mirrored. If mirrorMask is null, mirrors every digit.
export const applyMirror = (digits, mirrorMask = null) =>
  digits.map((d, i) => (mirrorMask ? (mirrorMask[i] ? mirrorDigit(d) : d) : mirrorDigit(d)));

// Date sum: ones-of-month + ones-of-day, reduced to a single digit.
// Example: Oct 5  -> month=10, day=5.  ones-of-month=0, ones-of-day=5, raw=5,  sum=5.
// Example: Oct 11 -> month=10, day=11. ones-of-month=0, ones-of-day=1, raw=1,  sum=1.
export const computeDateSum = (dateStr) => {
  const d = new Date(dateStr + "T00:00:00");
  const month = d.getMonth() + 1; // 1-12
  const day = d.getDate();
  const monthOnes = month % 10;
  const dayTens = Math.floor(day / 10);
  const dayOnes = day % 10;
  const rawSum = monthOnes + dayOnes;
  const reduceDigit = (n) => {
    let x = Math.abs(n);
    while (x > 9) {
      x = String(x).split("").reduce((s, c) => s + Number(c), 0);
    }
    return x;
  };
  const sumDigit = reduceDigit(rawSum);
  return {
    month,
    day,
    monthOnes,
    dayTens,
    dayOnes,
    rawSum,
    sumDigit,
    altSum: reduceDigit(month + day),
  };
};

// Enumerate every maximal line in the grid in each direction.
// 8 directions: row →, row ←, col ↓, col ↑, diag ↘, diag ↖, diag ↙, diag ↗.
const maximalLines = (columns) => {
  const C = columns.length;
  const R = columns[0].length;
  const lines = [];
  // rows (→ and ←)
  for (let r = 0; r < R; r++) {
    const cells = [];
    for (let c = 0; c < C; c++) cells.push([c, r]);
    lines.push({ dir: "row →", cells });
    lines.push({ dir: "row ←", cells: [...cells].reverse() });
  }
  // cols (↓ and ↑)
  for (let c = 0; c < C; c++) {
    const cells = [];
    for (let r = 0; r < R; r++) cells.push([c, r]);
    lines.push({ dir: "col ↓", cells });
    lines.push({ dir: "col ↑", cells: [...cells].reverse() });
  }
  // diag ↘ (and reverse ↖)
  const starts_dr = [];
  for (let c = 0; c < C; c++) starts_dr.push([c, 0]);
  for (let r = 1; r < R; r++) starts_dr.push([0, r]);
  for (const [sc, sr] of starts_dr) {
    const cells = [];
    let c = sc, r = sr;
    while (c < C && r < R) { cells.push([c, r]); c++; r++; }
    if (cells.length >= 2) {
      lines.push({ dir: "diag ↘", cells });
      lines.push({ dir: "diag ↖", cells: [...cells].reverse() });
    }
  }
  // diag ↙ (and reverse ↗)
  const starts_dl = [];
  for (let c = C - 1; c >= 0; c--) starts_dl.push([c, 0]);
  for (let r = 1; r < R; r++) starts_dl.push([C - 1, r]);
  for (const [sc, sr] of starts_dl) {
    const cells = [];
    let c = sc, r = sr;
    while (c >= 0 && r < R) { cells.push([c, r]); c--; r++; }
    if (cells.length >= 2) {
      lines.push({ dir: "diag ↙", cells });
      lines.push({ dir: "diag ↗", cells: [...cells].reverse() });
    }
  }
  return lines;
};

// Build the list of possible hits.
// Rule: a "hot line" is any maximal grid line (row, column, or diagonal, in
// either direction) that contains at least one anchor cell. An anchor cell
// has digit equal to the date-sum digit OR its mirror — nothing else is added
// to the anchor set.
// Every N-position ordered subsequence of a hot line is a possible hit. The
// anchor shows the player WHERE to look; the picked digits do not have to be
// the anchor itself.
export const buildCandidateHits = (
  seedDigits,
  dateSumDigit,
  targetLength = seedDigits.length,
) => {
  const columns = buildRollingColumns(seedDigits);
  const C = columns.length;
  const R = columns[0].length;
  const mirrorSum = mirrorDigit(dateSumDigit);
  const N = targetLength;
  const isAnchorDigit = (d) => d === dateSumDigit || d === mirrorSum;

  const anchorCells = [];
  for (let c = 0; c < C; c++) {
    for (let r = 0; r < R; r++) {
      if (isAnchorDigit(columns[c][r])) anchorCells.push([c, r, columns[c][r]]);
    }
  }

  const lines = maximalLines(columns);
  const hits = [];
  const seen = new Set();

  for (const { dir, cells } of lines) {
    const L = cells.length;
    if (L < N) continue;
    const hasAnchor = cells.some(([c, r]) => isAnchorDigit(columns[c][r]));
    if (!hasAnchor) continue;

    // Enumerate every length-N ordered subsequence of this line.
    const chosen = [];
    const walk = (start) => {
      if (chosen.length === N) {
        const picked = chosen.map((i) => cells[i]);
        let combo = "";
        for (const [c, r] of picked) combo += columns[c][r];
        const sortedKey = [...picked]
          .sort((p, q) => p[0] - q[0] || p[1] - q[1])
          .map((p) => `${p[0]},${p[1]}`)
          .join("|");
        const sig = `${combo}-${sortedKey}-${dir}`;
        if (seen.has(sig)) return;
        seen.add(sig);
        const isStraight = chosen.every((v, i) => i === 0 || v - chosen[i - 1] === 1);
        // index of the first anchor cell within the pick, if any
        const anchorIdx = picked.findIndex(([c, r]) => isAnchorDigit(columns[c][r]));
        hits.push({ combo, cells: picked, anchorIdx, dir, isStraight });
        return;
      }
      for (let i = start; i <= L - (N - chosen.length); i++) {
        chosen.push(i);
        walk(i + 1);
        chosen.pop();
      }
    };
    walk(0);
  }

  return { hits, anchorCells, anchorDigits: [dateSumDigit, mirrorSum] };
};

// Enumerate every mirror substitution of a draw string. For digits d1 d2 ...
// each digit can be itself or its mirror; returns the 2^N unique variants.
export const mirrorVariants = (drawStr) => {
  const clean = String(drawStr || "").replace(/\D/g, "");
  if (!clean) return [];
  const digits = clean.split("").map(Number);
  const results = new Set();
  const total = 1 << digits.length;
  for (let mask = 0; mask < total; mask++) {
    let out = "";
    for (let i = 0; i < digits.length; i++) {
      out += (mask >> i) & 1 ? mirrorDigit(digits[i]) : digits[i];
    }
    results.add(out);
  }
  return Array.from(results);
};

const pickBeforeAfter = null; // deprecated — kept null to avoid breaking imports

// Lucky pick: use only digits that appear in seed, date sum, or their mirrors.
export const luckyPick = (seedDigits, dateSumDigit, length) => {
  const pool = new Set();
  seedDigits.forEach((d) => {
    pool.add(d);
    pool.add(mirrorDigit(d));
  });
  pool.add(dateSumDigit);
  pool.add(mirrorDigit(dateSumDigit));
  const poolArr = Array.from(pool);
  const out = [];
  for (let i = 0; i < length; i++) {
    out.push(poolArr[Math.floor(Math.random() * poolArr.length)]);
  }
  return out.join("");
};

export const todayISO = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const formatDateLong = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};
